import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import {
  OrganizationPlanType,
  StripeProduct,
} from '@cartwhl/common/types';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';
import Text from '@cartwhl/ui/base/Text';
import CopyIcon from '@cartwhl/ui/icons/CopyIcon';
import EyeIcon from '@cartwhl/ui/icons/EyeIcon';
import MailIcon from '@cartwhl/ui/icons/MailIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import SortIcon from '@cartwhl/ui/icons/SortIcon';
import Button from '@cartwhl/ui/input/Button';

import { useModal } from 'modals';
import {
  customerStore,
  noticesStore,
  productStore,
  userStore,
} from 'stores/global';

import AddSeatModal from './AddSeatModal';
import AssignSeatModal from './AssignSeatModal';
import RemoveTeamMemberModal from './RemoveTeamMemberModal';
import TeamMemberRow, { TeamMemberListItem } from './TeamMemberRow';
import UserDetails from './UserDetails';

const ORG_PLAN_TYPES_HIDING_API_KEYS: ReadonlyArray<OrganizationPlanType> = [
  'individual_basic',
  'legacy_free',
  'legacy_pro',
  'legacy_unlimited',
];

function orgPlanHidesApiKeys(planType: OrganizationPlanType | undefined) {
  return (
    planType !== undefined &&
    ORG_PLAN_TYPES_HIDING_API_KEYS.includes(planType)
  );
}

export default function MyTeam() {
  const { open: openContactModal } = useModal();
  const {
    apiKey: apiKeyState,
    organizationInfo,
    organizationMembers,
    loading: userLoading,
    userInfo,
  } = useSnapshot(userStore.state);
  const [apiKeyRevealed, setApiKeyRevealed] = useState(false);
  const { customer, loading: customerLoading } = useSnapshot(
    customerStore.state,
  );
  const [requesting, setRequesting] = useState(false);

  const [openModal, setOpenModal] = useState<
    'addSeat' | 'assignSeat' | 'revokeSeat' | 'inviteMember' | null
  >(null);
  const [assignPlanType, setAssignPlanType] = useState<
    'pro' | 'unlimited' | null
  >(null);
  const [selectedMember, setSelectedMember] = useState<
    StripeProduct | any | null
  >(null);

  useEffect(() => {
    if (userInfo.stripeCustomerID) {
      customerStore.fetchCustomer(userInfo.stripeCustomerID);
    }
  }, [userInfo.stripeCustomerID]);

  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  // Fetch organization details once on mount, if available
  useEffect(() => {
    if (userInfo.organizationID) {
      userStore.fetchOrganization(userInfo.organizationID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch API key when user has an organization and key not yet loaded.
  // Wait for organizationInfo so we do not request keys for org plans that hide API keys in the UI.
  useEffect(() => {
    if (
      !userInfo.organizationID ||
      apiKeyState.status !== 'initial' ||
      !organizationInfo ||
      orgPlanHidesApiKeys(organizationInfo.planType)
    ) {
      return;
    }
    userStore.fetchApiKey();
  }, [
    userInfo.organizationID,
    apiKeyState.status,
    organizationInfo,
    organizationInfo?.planType,
  ]);

  const items: TeamMemberListItem[] = useMemo(() => {
    const list: TeamMemberListItem[] = [];
    const unassignedPro = Math.max(
      (organizationInfo?.totalProfessionalSeats ?? 0) -
        (organizationInfo?.activeProfessionalSeats ?? 0),
      0,
    );
    const unassignedUnl = Math.max(
      (organizationInfo?.totalUnlimitedSeats ?? 0) -
        (organizationInfo?.activeUnlimitedSeats ?? 0),
      0,
    );

    for (let i = 0; i < unassignedPro; i += 1) {
      list.push({
        accessLevel: undefined,
        kind: 'unassigned',
        plan: 'Professional',
        key: `unassigned-pro-${i}`,
      });
    }
    for (let i = 0; i < unassignedUnl; i += 1) {
      list.push({
        accessLevel: undefined,
        kind: 'unassigned',
        plan: 'Unlimited',
        key: `unassigned-unl-${i}`,
      });
    }

    (organizationMembers ?? []).forEach((member) => {
      const plan =
        member.planType === 'pro'
          ? 'Professional'
          : member.planType === 'unlimited'
            ? 'Unlimited'
            : 'Free';
      list.push({
        accessLevel: member.accessLevel === 'EDIT' ? 'Admin' : 'Member',
        kind: 'member',
        plan,
        user: member,
        key: member.userID,
      });
    });

    return list;
  }, [organizationInfo, organizationMembers]);

  if (!organizationInfo) {
    return null;
  }

  async function requestPortalSession() {
    setRequesting(true);
    getStripePortalSession(userInfo.stripeCustomerID)
      .then(({ data }) => {
        if (typeof data !== 'undefined') {
          window.location.href = data.url;
        }
        setRequesting(false);
      })
      .catch(() => setRequesting(false));
  }

  const totalSeats: number =
    (organizationInfo.totalProfessionalSeats ?? 0) +
    (organizationInfo.totalUnlimitedSeats ?? 0);

  const activeProfessionalSeats: number =
    organizationInfo.activeProfessionalSeats ?? 0;
  const activeUnlimitedSeats: number =
    organizationInfo.activeUnlimitedSeats ?? 0;

  const unassignedProfessionalSeats: number =
    (organizationInfo.totalProfessionalSeats ?? 0) - activeProfessionalSeats;
  const unassignedUnlimitedSeats: number =
    (organizationInfo.totalUnlimitedSeats ?? 0) - activeUnlimitedSeats;

  const hasEditAccess = organizationInfo.accessLevel === 'EDIT';

  if (userLoading || customerLoading) {
    return (
      <div className="overview max-w-(--breakpoint-md) mx-auto">
        <div className="animate-pulse">
          <div className="h-24 w-24 rounded-full bg-gray-200" />
          <div className="mt-4 h-6 w-48 bg-gray-200" />
          <div className="mt-2 h-4 w-32 bg-gray-200" />
        </div>
        <div className="mt-8">
          <div className="h-6 w-32 bg-gray-200" />
          <div className="mt-4 rounded-lg border p-6">
            <div className="h-8 w-full bg-gray-200" />
            <div className="mt-4 h-8 w-full bg-gray-200" />
          </div>
        </div>
        <div className="mt-8">
          <div className="h-6 w-32 bg-gray-200" />
          <div className="mt-4 rounded-lg border p-6">
            <div className="h-8 w-full bg-gray-200" />
            <div className="mt-4 h-8 w-full bg-gray-200" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overview max-w-(--breakpoint-md) mx-auto">
      <UserDetails userInfo={userInfo} />
      {hasEditAccess && (
        <div className="mt-8 flex flex-col justify-between gap-4">
          <div className="mb-4 flex flex-row justify-between gap-4">
            <Text as="h3" variant="studio/body/medium/sm" className="mb-4">
              {organizationInfo.organizationName} Dashboard
            </Text>
            <div className="flex flex-row gap-2">
              <Button
                disabled={!hasEditAccess}
                icon={<PlusIcon />}
                variant="secondary"
                size="sm"
                onClick={() => setOpenModal('addSeat')}
              >
                {`Add seats`}
              </Button>

              <Button
                disabled={!hasEditAccess}
                variant="secondary"
                loading={requesting}
                size="sm"
                onClick={requestPortalSession}
              >
                {`Manage seats`}
              </Button>
            </div>
          </div>
          <div className="border-gray-30 dark:border-gray-60 relative rounded-lg border p-6">
            <div className="border-gray-30 dark:border-gray-60 flex flex-row justify-between gap-4 border-b pb-2">
              <Text
                as="p"
                variant="studio/body/md"
                className="mt-2 p-2 opacity-60 dark:opacity-70"
              >
                Total seats
              </Text>

              <Text
                as="p"
                variant="studio/body/medium/sm"
                className="bg-blue/10 text-blue mt-2 rounded-lg p-2"
              >
                {totalSeats}
              </Text>
            </div>

            <div className="border-gray-30 dark:border-gray-60 flex flex-row justify-between gap-4 border-b pb-2">
              <Text
                as="p"
                variant="studio/body/md"
                className="mt-2 p-2 opacity-60 dark:opacity-70"
              >
                Assigned seats
              </Text>
              <div className="flex flex-row gap-2">
                {activeProfessionalSeats > 0 && (
                  <>
                    <Text
                      as="p"
                      variant="studio/body/md"
                      className="mt-2 p-2 opacity-60 dark:opacity-70"
                    >
                      Professional
                    </Text>
                    <Text
                      as="p"
                      variant="studio/body/medium/sm"
                      className="bg-gray-15 mt-2 rounded-lg p-2"
                    >
                      {activeProfessionalSeats}
                    </Text>
                  </>
                )}
                {activeProfessionalSeats > 0 && activeUnlimitedSeats > 0 && (
                  <Text
                    as="p"
                    variant="studio/body/md"
                    className="mt-2 p-2 opacity-60 dark:opacity-70"
                  >
                    /
                  </Text>
                )}
                <Text
                  as="p"
                  variant="studio/body/md"
                  className="mt-2 p-2 opacity-60 dark:opacity-70"
                >
                  Unlimited
                </Text>
                <Text
                  as="p"
                  variant="studio/body/medium/sm"
                  className="bg-gray-15 mt-2 rounded-lg p-2"
                >
                  {activeUnlimitedSeats}
                </Text>
              </div>
            </div>

            <div className="flex flex-row justify-between gap-4 ">
              <Text
                as="p"
                variant="studio/body/md"
                className="mt-2 p-2 opacity-60 dark:opacity-70"
              >
                Unassigned seats
              </Text>
              <div className="flex flex-row gap-2">
                {unassignedProfessionalSeats > 0 && (
                  <>
                    <Text
                      as="p"
                      variant="studio/body/md"
                      className="mt-2 p-2 opacity-60 dark:opacity-70"
                    >
                      Professional
                    </Text>
                    <Text
                      as="p"
                      variant="studio/body/medium/sm"
                      className="bg-gray-15 mt-2 rounded-lg p-2"
                    >
                      {unassignedProfessionalSeats}
                    </Text>
                  </>
                )}
                {unassignedProfessionalSeats > 0 &&
                  unassignedUnlimitedSeats > 0 && (
                    <Text
                      as="p"
                      variant="studio/body/md"
                      className="mt-2 p-2 opacity-60 dark:opacity-70"
                    >
                      /
                    </Text>
                  )}
                {unassignedUnlimitedSeats > 0 && (
                  <>
                    <Text
                      as="p"
                      variant="studio/body/md"
                      className="mt-2 p-2 opacity-60 dark:opacity-70"
                    >
                      Unlimited
                    </Text>
                    <Text
                      as="p"
                      variant="studio/body/medium/sm"
                      className="bg-gray-15 mt-2 rounded-lg p-2"
                    >
                      {unassignedUnlimitedSeats}
                    </Text>
                  </>
                )}
                {unassignedProfessionalSeats === 0 &&
                  unassignedUnlimitedSeats === 0 && (
                    <Text
                      as="p"
                      variant="studio/body/medium/sm"
                      className="bg-gray-15 mt-2 rounded-lg p-2"
                    >
                      0
                    </Text>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="mb-4 flex flex-row justify-between gap-4">
          <Text as="h3" variant="studio/body/md">
            {organizationInfo.organizationName} Members
          </Text>
          <div className="flex flex-row gap-2">
            <Button
              icon={<SortIcon />}
              variant="secondary"
              size="sm"
              onClick={() => noticesStore.info('Filtering coming soon')}
            />

            <Button
              disabled={!hasEditAccess}
              icon={<PlusIcon />}
              variant="secondary"
              size="sm"
              onClick={() => noticesStore.info('Feature coming soon')}
            >
              {`Invite new user`}
            </Button>
          </div>
        </div>
        <div className="border-gray-30 dark:border-gray-60 relative mt-4 rounded-lg border p-6">
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_96px] items-start gap-4">
            <Text
              as="p"
              variant="studio/body/bold/md"
              className="opacity-60 dark:opacity-70"
            >
              Name
            </Text>
            <Text
              as="p"
              variant="studio/body/bold/md"
              className="opacity-60 dark:opacity-70"
            >
              Plan type
            </Text>
            <Text
              as="p"
              variant="studio/body/bold/md"
              className="opacity-60 dark:opacity-70"
            >
              Access level
            </Text>
            {hasEditAccess && (
              <Text
                as="p"
                variant="studio/body/bold/md"
                className="flex justify-end opacity-70"
              >
                Action
              </Text>
            )}

            {items.map((item) => (
              <TeamMemberRow
                hasEditAccess={hasEditAccess}
                key={item.key}
                item={item}
                onAssign={(plan) => {
                  const type = plan === 'Unlimited' ? 'unlimited' : 'pro';
                  setAssignPlanType(type);
                  setOpenModal('assignSeat');
                }}
                onEdit={(member) => {
                  setSelectedMember(member);
                  setOpenModal('revokeSeat');
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {!orgPlanHidesApiKeys(organizationInfo.planType) && (
        <div className="mt-8">
          <Text as="h3" variant="studio/body/md" className="mb-4">
            Cartwheel API Keys
          </Text>
          <div className="border-gray-30 dark:border-gray-60 rounded-lg border p-6">
            {apiKeyState.status === 'pending' && (
              <div className="flex animate-pulse items-center gap-4">
                <div className="dark:bg-gray-60 h-5 max-w-xs flex-1 rounded bg-gray-200" />
                <div className="dark:bg-gray-60 h-9 w-20 rounded bg-gray-200" />
              </div>
            )}
            {(apiKeyState.status === 'error' ||
              (apiKeyState.status === 'ready' && !apiKeyState.data)) && (
              <div className="flex flex-col gap-4">
                {apiKeyState.status === 'error' && apiKeyState.error && (
                  <Text
                    as="p"
                    variant="studio/body/md"
                    className="text-red-600 dark:text-red-400"
                  >
                    {apiKeyState.error}
                  </Text>
                )}
                <div className="flex flex-col gap-2">
                  <Text
                    as="p"
                    variant="studio/body/md"
                    className="opacity-80 dark:opacity-80"
                  >
                    Interested in using the API?
                  </Text>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      openContactModal('ENTERPRISE_CONTACT', {
                        reason: 'request_api_access',
                      })
                    }
                  >
                    Request Access
                  </Button>
                </div>
              </div>
            )}
            {apiKeyState.status === 'ready' && apiKeyState.data && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  <code className="bg-gray-15 dark:bg-gray-60 min-w-0 flex-1 break-all rounded px-3 py-2 font-mono text-sm">
                    {apiKeyRevealed
                      ? apiKeyState.data.apiKey
                      : `••••••••••••${apiKeyState.data.apiKey.slice(-4)}`}
                  </code>
                  <div className="ml-auto flex flex-shrink-0 gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<CopyIcon />}
                      onClick={async () => {
                        await navigator.clipboard.writeText(
                          apiKeyState.data!.apiKey,
                        );
                        noticesStore.info('API key copied to clipboard');
                      }}
                    >
                      Copy
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<EyeIcon />}
                      onClick={() => setApiKeyRevealed((v) => !v)}
                    >
                      {apiKeyRevealed ? 'Hide' : 'Reveal'}
                    </Button>
                  </div>
                </div>
                {apiKeyState.data.serviceID && (
                  <Text
                    as="p"
                    variant="studio/body/sm"
                    className="opacity-60 dark:opacity-70"
                  >
                    Service ID: {apiKeyState.data.serviceID}
                  </Text>
                )}
              </div>
            )}
            {apiKeyState.status === 'initial' && (
              <Text
                as="p"
                variant="studio/body/md"
                className="opacity-60 dark:opacity-70"
              >
                Loading…
              </Text>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-col items-center justify-center gap-4">
        <Text as="h3" variant="studio/body/sm">
          Need help? {!hasEditAccess ? 'Contact your team admin.' : ''}
        </Text>
        {hasEditAccess ? (
          <a
            href="mailto:pros@cartwheel.com?subject=Help%20with%20Team%20Dashboard"
            style={{ textDecoration: 'none' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button icon={<MailIcon />} variant="secondary" size="lg">
              Contact support
            </Button>
          </a>
        ) : (
          <Button
            className="flex w-full"
            variant="secondary-destructive"
            size="lg"
            onClick={() => {
              setOpenModal('revokeSeat');
              setSelectedMember(userInfo);
            }}
          >
            Leave team
          </Button>
        )}
      </div>

      {openModal === 'addSeat' && (
        <AddSeatModal close={() => setOpenModal(null)} />
      )}
      {openModal === 'assignSeat' && assignPlanType && (
        <AssignSeatModal
          close={() => {
            setOpenModal(null);
            setAssignPlanType(null);
          }}
          planType={assignPlanType}
        />
      )}
      {openModal === 'revokeSeat' && selectedMember && (
        <RemoveTeamMemberModal
          close={() => {
            setOpenModal(null);
            setSelectedMember(null);
          }}
          member={selectedMember}
        />
      )}
    </div>
  );
}
